<script setup>
// O e-mail desenhado no navegador, com as mesmas medidas do e-mail final.
// Linhas arrastáveis (grupo "rows"); o rodapé fica fora da lista (fixo no fim).
import { computed, inject, provide, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import Draggable from 'vuedraggable';
import { isFooterRow } from '../document/ops';
import { docWidth, themeColor } from './canvasStyles';
import CanvasRow from './CanvasRow.vue';

const ed = inject('emailEditor');
const { t } = useI18n();
provide('canvasEditing', ref(null));

const doc = computed(() => ed.doc.value);
const theme = computed(() => ed.template.value?.theme || {});
const bodyRows = computed(() => doc.value.rows.filter(r => !isFooterRow(r)));
const footer = computed(() => doc.value.rows.find(isFooterRow));
const width = computed(() => docWidth(doc.value, ed.device.value));
const outer = computed(
  () =>
    themeColor(
      doc.value.settings?.background_outer || 'surface',
      theme.value
    ) || '#f6f8fb'
);
const inner = computed(
  () =>
    themeColor(doc.value.settings?.background_inner || 'white', theme.value) ||
    '#ffffff'
);

/* eslint-disable no-underscore-dangle */
const onRowsChange = evt => {
  if (evt.added) {
    const item = evt.added.element;
    if (item.__structure) ed.addStructure(item.__structure, evt.added.newIndex);
    else if (item.__section) ed.addSection(item.__section, evt.added.newIndex);
    else if (item.__saved) {
      ed.insertSaved(item.__saved, { rowIndex: evt.added.newIndex });
    }
  }
  if (evt.moved) ed.moveRow(evt.moved.element.id, evt.moved.newIndex);
};
</script>

<template>
  <div
    data-canvas-bg
    class="min-h-full overflow-auto px-6 py-8 transition-colors"
    :style="{ backgroundColor: outer }"
    @click="ed.select(null)"
  >
    <div
      class="mx-auto overflow-hidden shadow-sm transition-[width] duration-300"
      :style="{
        width: `${width}px`,
        backgroundColor: inner,
        borderRadius: `${doc.settings?.radius ?? 12}px`,
      }"
    >
      <Draggable
        :model-value="bodyRows"
        item-key="id"
        :group="{ name: 'rows', pull: true, put: ['rows'] }"
        handle="[data-row-handle]"
        ghost-class="opacity-40"
        :animation="180"
        @change="onRowsChange"
      >
        <template #item="{ element }">
          <CanvasRow :row="element" :width="width" />
        </template>
        <template #footer>
          <p
            v-if="!bodyRows.length"
            class="px-6 py-16 text-center text-sm text-n-slate-10"
          >
            {{ t('EMAIL_TEMPLATES.EDITOR.CANVAS.EMPTY_EMAIL') }}
          </p>
        </template>
      </Draggable>
      <CanvasRow v-if="footer" :row="footer" :width="width" />
    </div>
  </div>
</template>
